import React, { useEffect, useRef, useState } from 'react';
import { sortStringsAlphabetically } from '../utils';
import { ReactComponent as ArrowDownIcon } from '../assets/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../assets/arrow_up.svg';

const DropdownField = ({
  placeholder = 'Select...',
  values,
  onUpdate,
  initialValue,
  allowFilter = true,
  allowCustom = false,
  canClear = false,
  displayValues,
}: {
  placeholder?: string;
  displayValues?: string[];
  values: string[];
  onUpdate: (name: string) => void;
  initialValue?: string;
  allowFilter?: boolean;
  allowCustom?: boolean;
  canClear?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [filter, setFilter] = useState(initialValue || '');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const [interactedAfterOpened, setInteractedAfterOpened] = useState(false);

  useEffect(() => {
    if (initialValue) {
      setFilter(initialValue);
    }
  }, [initialValue]);

  const filteredValues = values.filter(name => name.toLowerCase().includes(filter.toLowerCase()));

  const filteredDisplayValues = displayValues?.filter(name => name.toLowerCase().includes(filter.toLowerCase()));

  const handleInputChange = (e: any) => {
    setFilter(e.target.value);
    setIsDropdownVisible(true); // Show dropdown when typing
    setInteractedAfterOpened(true);
  };

  const handleBlur = () => {
    if (allowCustom) {
      onUpdate(filter);
    }

    setIsDropdownVisible(false);
  };

  const handleDivClick = () => {
    if (isDropdownVisible) {
      setIsDropdownVisible(false);
    } else {
      setInteractedAfterOpened(false);

      if (inputRef.current) {
        inputRef.current.focus();
      }

      setIsDropdownVisible(true);
    }
  };

  const handleSelect = (name: string) => {
    onUpdate(name);
    setFilter(name);
    setIsDropdownVisible(false);
  };

  const getDisplayValue = (value: string) => {
    if (!displayValues) {
      return value;
    }

    return values.indexOf(value) !== -1 ? displayValues?.[values.indexOf(value)] : value;
  };

  const getTrueValue = (value: string) => {
    if (!displayValues) {
      return value;
    }

    return displayValues.indexOf(value) !== -1 ? values?.[displayValues.indexOf(value)] : value;
  };

  return (
    <div style={styles.container}>
      <div
        tabIndex={1}
        onClick={handleDivClick}
        onBlur={handleBlur}
        style={{
          ...{
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexDirection: 'row' as 'row',
            alignItems: 'center',
          },
          ...styles.select,
        }}
      >
        {allowFilter ? (
          <input
            ref={inputRef}
            type="text"
            style={styles.input}
            value={filter}
            onChange={handleInputChange}
            placeholder={placeholder}
            onKeyDown={e => {
              if (allowCustom && e.key === 'Enter') {
                onUpdate(filter);
              }
            }}
            onBlur={e => {
              e.stopPropagation();
            }}
          />
        ) : (
          <>
            <span
              style={{
                fontSize: '1.4vh',
                fontWeight: '400',
              }}
            >
              {getDisplayValue(filter)}
            </span>
            <div style={{ flex: 1 }}></div>
          </>
        )}
        {isDropdownVisible ? (
          <ArrowUpIcon style={{ width: '2.4vh', height: '2.4vh', color: 'rgba(16, 29, 59, 0.6)' }} />
        ) : (
          <ArrowDownIcon style={{ width: '2.4vh', height: '2.4vh', color: 'rgba(16, 29, 59, 0.6)' }} />
        )}

        {isDropdownVisible && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              background: 'transparent',
            }}
            onClick={() => handleBlur()}
          />
        )}

        {isDropdownVisible && (
          <div style={{ ...styles.dropdown, ...styles.scrollbar }} className="scrollable-container">
            {canClear && (
              <div
                style={{ ...styles.option, justifyContent: 'center' }}
                onMouseDown={e => {
                  e.stopPropagation();

                  handleSelect('');
                }}
              >
                clear
              </div>
            )}

            {(interactedAfterOpened ? filteredValues : values).length === 0 ? (
              canClear ? (
                <></>
              ) : (
                <div style={{ ...styles.option, justifyContent: 'center' }}>nothing found</div>
              )
            ) : displayValues ? (
              sortStringsAlphabetically(interactedAfterOpened ? filteredDisplayValues! : displayValues).map(market => (
                <div
                  key={getTrueValue(market)}
                  style={styles.option}
                  onMouseDown={e => {
                    e.stopPropagation();

                    handleSelect(getTrueValue(market));
                  }} // Use onMouseDown to prevent blur before click
                >
                  {market}
                </div>
              ))
            ) : (
              sortStringsAlphabetically(interactedAfterOpened ? filteredValues : values).map(market => (
                <div
                  key={market}
                  style={styles.option}
                  onMouseDown={e => {
                    e.stopPropagation();

                    handleSelect(market);
                  }} // Use onMouseDown to prevent blur before click
                >
                  {getDisplayValue(market)}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    display: 'flex',
  },
  select: {
    padding: '1.2vh',
    borderRadius: '1.6vh',
    border: '0.1vh solid rgba(16, 29, 59, 0.2)',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as 'border-box',
  },
  input: {
    fontSize: '1.4vh',
    fontWeight: '400',
    border: 'none',
    backgroundColor: '#ffffff',
    color: 'rgba(16, 29, 59, 1)',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    outline: 'none',
    width: '100%',
  },
  dropdown: {
    position: 'absolute' as 'absolute',
    padding: '0vh 1.6vh',
    top: 'calc(100% - 0.6vh)', // Position it right below the input
    left: '0',
    width: 'calc(100% - 3.2vh)', //compensate padding
    borderTop: 'none',
    maxHeight: '15vh',
    overflowY: 'auto' as 'auto',
    backgroundColor: '#ffffff',
    zIndex: 100,
    borderRadius: '1.6vh',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
  },
  option: {
    padding: '0.8vh 0vh',
    fontWeight: '400',
    fontSize: '1.4vh',
    cursor: 'pointer',
    borderBottom: '0.1vh solid #eee',
    color: '#2d3436',
    textAlign: 'left' as 'left',
    height: '3.2vh',
    display: 'flex',
    alignItems: 'center',
  },
  scrollbar: {
    // Empty for now due to different browser specification complexity
  },
};

export default DropdownField;
